import { IValidationConfig, CrossCheckValidatorConfig } from '../types/my-types';

export const REQUIRED_FIELD_VALIDATOR = 'RequiredFieldValidator';
export const CURRENT_CHARGES_SUMCHECK_VALIDATOR = 'CrossCheckValidator';

// Default configuration for charges sumcheck
export const DEFAULT_SUMCHECK_CONFIG = {
  include: [
    {
      pattern: 'charge',
      attribute: 'tags',
    },
  ],
  exclude: [
    {
      pattern: 'totalsupplycharge',
      attribute: 'tags',
    },
    {
      pattern: 'totaldeliverycharge',
      attribute: 'tags',
    },
    {
      pattern: 'CurrentCharges',
      attribute: 'label',
    },
  ],
} as const;

export function isFieldRequired(config: IValidationConfig[] | null | undefined): boolean {
  if (!config || !config.length) {
    return false;
  }

  return config?.some((validation) => validation.opname === REQUIRED_FIELD_VALIDATOR);
}

export function removeRequiredFieldValidator(
  config: IValidationConfig[] | null | undefined
): IValidationConfig[] {
  if (!config) {
    return [];
  }

  return config.filter((validation) => validation.opname !== REQUIRED_FIELD_VALIDATOR);
}

export function addRequiredFieldValidator(
  config: IValidationConfig[] | null | undefined
): IValidationConfig[] {
  const validatorObject = {
    opname: REQUIRED_FIELD_VALIDATOR,
  };

  // If config is not set, return the (default) required field validator
  if (!config) {
    return [validatorObject];
  }

  // Check if the required field validator is already present
  const existingValidation = config.find(
    (validation) => validation.opname === validatorObject.opname
  );

  // If the existing required config is found, return the existing config
  if (existingValidation) {
    return config;
  }

  // Add the required field validator to the existing config
  return [...config, validatorObject];
}

export function fieldHasCurrentChargesSumCheck(
  config: IValidationConfig[] | null | undefined
): boolean {
  if (!config || !config.length) {
    return false;
  }

  return config?.some((validation) => validation.opname === CURRENT_CHARGES_SUMCHECK_VALIDATOR);
}

export interface SumcheckPatternConfig {
  pattern: string;
  attribute: string;
}

export interface SumcheckConfigInput {
  include: SumcheckPatternConfig[];
  exclude: SumcheckPatternConfig[];
}

/**
 * Compares two sumcheck configs to determine if they are different.
 * Returns true if the config differs from the default.
 */
export function isCustomSumcheckConfig(config: SumcheckConfigInput | null | undefined): boolean {
  if (!config) {
    return false;
  }

  const defaultInclude = DEFAULT_SUMCHECK_CONFIG.include;
  const defaultExclude = DEFAULT_SUMCHECK_CONFIG.exclude;

  // Check if include patterns differ
  if (config.include.length !== defaultInclude.length) {
    return true;
  }

  for (let i = 0; i < config.include.length; i++) {
    const configPattern = config.include[i];
    const defaultPattern = defaultInclude[i];
    if (
      configPattern.pattern !== defaultPattern.pattern ||
      configPattern.attribute !== defaultPattern.attribute
    ) {
      return true;
    }
  }

  // Check if exclude patterns differ
  if (config.exclude.length !== defaultExclude.length) {
    return true;
  }

  for (let i = 0; i < config.exclude.length; i++) {
    const configPattern = config.exclude[i];
    const defaultPattern = defaultExclude[i];
    if (
      configPattern.pattern !== defaultPattern.pattern ||
      configPattern.attribute !== defaultPattern.attribute
    ) {
      return true;
    }
  }

  return false;
}

export function getExistingSumcheckConfig(
  config: IValidationConfig[] | null | undefined
): SumcheckConfigInput | null {
  if (!config || !config.length) {
    return null;
  }

  const existingValidation = config.find(
    (validation) => validation.opname === CURRENT_CHARGES_SUMCHECK_VALIDATOR
  );

  if (existingValidation?.config) {
    return {
      include: existingValidation.config.include || [],
      exclude: existingValidation.config.exclude || [],
    };
  }

  return null;
}

export function addCurrentChargesSumCheck(
  config: IValidationConfig[] | null | undefined,
  customConfig?: SumcheckConfigInput | null
): IValidationConfig[] {
  // Use custom config if provided, otherwise use default
  const sumcheckConfig = customConfig || DEFAULT_SUMCHECK_CONFIG;

  const validatorObject: IValidationConfig = {
    opname: CURRENT_CHARGES_SUMCHECK_VALIDATOR,
    config: {
      CONFIGNAME: 'CrossCheckValidatorConfig',
      label: 'CurrentCharges',
      include: sumcheckConfig.include.map((p) => ({
        pattern: p.pattern,
        attribute: p.attribute,
      })),
      exclude: sumcheckConfig.exclude.map((p) => ({
        pattern: p.pattern,
        attribute: p.attribute,
      })),
    },
  };

  // If config is not set, return the (default) required field validator
  if (!config) {
    return [validatorObject];
  }

  // Check if the required field validator is already present
  const existingIndex = config.findIndex(
    (validation) => validation.opname === validatorObject.opname
  );

  // If the existing required config is found, replace it with the new config
  if (existingIndex !== -1) {
    const newConfig = [...config];
    newConfig[existingIndex] = validatorObject;
    return newConfig;
  }

  // Add the required field validator to the existing config
  return [...config, validatorObject];
}

export function removeCurrentChargesSumCheck(
  config: IValidationConfig[] | null | undefined
): IValidationConfig[] {
  if (!config) {
    return [];
  }

  return config.filter((validation) => validation.opname !== CURRENT_CHARGES_SUMCHECK_VALIDATOR);
}
